/**
 * Order Details API
 * APIs for managing order details, items, and order actions
 * 
 * Base URL: https://api.innoworx.ai/Stage
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp > currentTime) return tokens.idToken.toString();
    }
  } catch (e) {
    console.error('OrderDetails API: auth error', e);
  }
  return null;
};

/**
 * Generic function to make authenticated API requests.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing. Please ensure a user is logged in.');
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.message || `API Error: ${response.status}`;
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_order_details',
        `Order Details API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[orderDetailsApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const corsError = new Error(
        `CORS Error: Unable to connect to the API. ` +
        'The API Gateway needs to be configured to allow CORS from your origin. ' +
        'Please contact the backend team to ensure CORS headers are properly configured.'
      );
      corsError.isCorsError = true;
      corsError.endpoint = endpoint;
      
      // Log CORS errors to logging service
      loggingService.logError(
        'api_error_order_details',
        `Order Details API CORS/network error for ${endpoint}`,
        corsError,
        {
          endpoint,
          method,
          errorType: 'CORS_ERROR',
        }
      ).catch(logError => {
        console.warn('[orderDetailsApi] Failed to log error:', logError);
      });
      
      throw corsError;
    }
    
    // Log network/fetch errors to logging service
    if (error.status === undefined) {
      loggingService.logError(
        'api_error_order_details',
        `Order Details API network/fetch error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[orderDetailsApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get full order + line items for one order.
 * GET /restaurant/order/items?order_id={order_id}
 * Lambda: innoworx-restaurant-get-order-items-for-order-id
 * 
 * Response: { order_id, order_details, order_items }
 * - order_details from Restaurant-Orders table
 * - order_items from Restaurant-Order-Items table
 * 
 * @param {string} order_id - The order ID
 * @returns {Promise<Object>} Order object with order_id, order_details, order_items
 */
export const getOrderItems = async (order_id) => {
  if (!order_id) {
    throw new Error('order_id is required');
  }

  const response = await makeApiRequest(`/restaurant/order/items?order_id=${order_id}`, 'GET');
  return await response.json();
};

/**
 * Get active order items (e.g. by shift/site).
 * GET /restaurant/order/get-active
 * Lambda: innoworx-restaurant-get-active-order-items
 * 
 * @param {Object} [params] - Optional query parameters
 * @param {string} [params.site_id] - Site ID filter
 * @param {string} [params.ss_id] - Shift schedule ID filter
 * @returns {Promise<Array>} Array of active order items
 */
export const getActiveOrderItems = async ({ site_id, ss_id } = {}) => {
  const params = new URLSearchParams();
  if (site_id) params.set('site_id', site_id);
  if (ss_id) params.set('ss_id', ss_id);

  const endpoint = `/restaurant/order/get-active${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await makeApiRequest(endpoint, 'GET');
  const data = await response.json();
  return Array.isArray(data) ? data : (data.items || data.order_items || []);
};

/**
 * Get open vs closed orders by shift.
 * GET /restaurant/order/open-and-close-orders
 * Lambda: innoworx-restaurant-get-finished-and-unfinished-orders-by-ss_id
 * 
 * @param {string} ss_id - Shift schedule ID
 * @returns {Promise<Object>} Object with open and closed orders: { open_orders: [], closed_orders: [] }
 */
export const getOpenAndCloseOrders = async (ss_id) => {
  if (!ss_id) {
    throw new Error('ss_id is required');
  }

  const response = await makeApiRequest(`/restaurant/order/open-and-close-orders?ss_id=${ss_id}`, 'GET');
  return await response.json();
};

/**
 * End order and get summary.
 * POST /restaurant/order/end
 * Lambda: innoworx-restaurant-end-order-and-get-order-summary
 * 
 * @param {string} order_id - The order ID to end
 * @returns {Promise<Object>} Order summary
 */
export const endOrder = async (order_id) => {
  if (!order_id) {
    throw new Error('order_id is required');
  }

  const body = { order_id };
  const response = await makeApiRequest('/restaurant/order/end', 'POST', body);
  return await response.json();
};

/**
 * Get all order IDs for a shift.
 * GET /restaurant/getall-orders-by-shift?ss_id={ss_id}
 * Lambda: innoworx-restaurant-getall-order_ids_by_ss_id
 * On 502/5xx (e.g. Lambda timeout) returns [] so the UI can degrade gracefully.
 *
 * @param {string} ss_id - Shift schedule ID
 * @returns {Promise<Array>} Array of order IDs, or [] on server error
 */
export const getAllOrdersByShift = async (ss_id) => {
  if (!ss_id) {
    throw new Error('ss_id is required');
  }

  try {
    const response = await makeApiRequest(`/restaurant/getall-orders-by-shift?ss_id=${encodeURIComponent(ss_id)}`, 'GET');
    const data = await response.json();
    return Array.isArray(data) ? data : (data.order_ids || data.orders || []);
  } catch (err) {
    if (err.status >= 500 || err.status === 502) {
      console.warn('getAllOrdersByShift: server error', err.status, err.message, '- returning empty list');
      return [];
    }
    throw err;
  }
};

/**
 * Delete a queued order item.
 * DELETE /restaurant/delete-order-item-if-status-queued
 * Lambda: innoworx-restaurant-delete-order-item-if-status-queued
 *
 * @param {Object} params
 * @param {string} params.order_id - The order ID
 * @param {string} params.order_item_id - The order item ID to delete
 * @returns {Promise<Object>} Deletion response
 */
export const deleteQueuedOrderItem = async ({ order_id, order_item_id } = {}) => {
  if (!order_id) {
    throw new Error('order_id is required');
  }
  if (!order_item_id) {
    throw new Error('order_item_id is required');
  }

  const body = { order_id, order_item_id };
  const response = await makeApiRequest('/restaurant/delete-order-item-if-status-queued', 'DELETE', body);
  return await response.json();
};

/**
 * Send ready notification for an order item.
 * POST /restaurant/send-ready-notification
 * Lambda: innoworx-restaurant-send-order-item-ready-notification
 * 
 * @param {string} order_item_id - The order item ID
 * @returns {Promise<Object>} Notification response
 */
export const sendReadyNotification = async (order_item_id) => {
  if (!order_item_id) {
    throw new Error('order_item_id is required');
  }

  const body = { order_item_id };
  const response = await makeApiRequest('/restaurant/send-ready-notification', 'POST', body);
  return await response.json();
};

/**
 * Set kitchen stage for an order item.
 * POST /restaurant/kitchen/set-stage
 * Lambda: innoworx-restaurant-set-order-stage
 * 
 * @param {Object} stageData - Stage data
 * @param {string} stageData.order_item_id - Order item ID
 * @param {string} stageData.stage - Stage name (e.g., 'queued', 'preparing', 'ready', 'served')
 * @param {string} [stageData.chef_assigned] - Chef user ID (optional)
 * @returns {Promise<Object>} Stage update response
 */
export const setOrderItemStage = async ({ order_item_id, stage, chef_assigned } = {}) => {
  if (!order_item_id) {
    throw new Error('order_item_id is required');
  }
  if (!stage) {
    throw new Error('stage is required');
  }

  const body = {
    order_item_id,
    stage,
    ...(chef_assigned && { chef_assigned }),
  };

  const response = await makeApiRequest('/restaurant/kitchen/set-stage', 'POST', body);
  return await response.json();
};

export default {
  getOrderItems,
  getActiveOrderItems,
  getOpenAndCloseOrders,
  endOrder,
  getAllOrdersByShift,
  deleteQueuedOrderItem,
  sendReadyNotification,
  setOrderItemStage,
};

